/**
 * usePortfolioData — THE portfolio data-intake hook: settings, the active
 * portfolio's trades, and the EOD price book, with loading/error state and a
 * re-fetch on portfolio change.
 *
 * Options:
 *   - `allPortfolios: true` — atomically include EVERY portfolio's trades in
 *     the mount Promise.all so aggregate views paint all funds first load (ADR-0017).
 *   - `prices: false` — skip the price book for pages that never value holdings.
 *   - `trades: false` — skip the active trade list for settings-only pages.
 *
 * STUCK-LOADING FIX: each load runs under a ticket from lib/dataIntake.ts (the
 * pure race rules) rather than a closure `cancelled` flag — the old pattern
 * could leave a remounted/StrictMode page stuck loading forever. The latest
 * load always wins and always clears `loading`; superseded runs drop at the
 * staleness checks so stale data can't overwrite fresh.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import {
  getTrades, getSettings, getFxStore, getAllPortfolioTrades,
  getCustomPrices, PortfolioApiError,
} from './portfolioApi';
import type { Trade, Settings, PriceStore, CustomPrices } from './portfolioApi';
import type { FxStore } from './fx';
import {
  alignQuoteUnits, composePriceBook, loadWarehouseSeries, loadUnpricedSeries, tradeUnits, unpricedHeld,
  fillUnpriced,
} from './priceBook';
import { everHeldTickers } from './calc/holdings';
import { fetchListingStatus } from './listingStatus';
import { isAllPortfolios } from './allPortfolios';
import { createLoadGenerations } from './dataIntake';
import { useActivePortfolio } from './activePortfolio';

export interface UsePortfolioDataOptions {
  /** Atomically fetch every portfolio's trades into `tradesByPortfolio` at
   *  mount (aggregate views). Default false. */
  allPortfolios?: boolean;
  /** Fetch + compose the price book (EOD store, FX, custom prices, warehouse
   *  overlay). Default true; set false for pages that never value holdings. */
  prices?: boolean;
  /** Fetch the active portfolio's trade list (and re-fetch it on portfolio
   *  switch). Default true; set false for settings-only pages. */
  trades?: boolean;
  /** Fetch the FX store on its own. Defaults to `prices` (the price book
   *  always includes FX); set true with `prices: false` for pages that never
   *  value holdings but still convert cash amounts into base (Dividends). */
  fx?: boolean;
}

export interface PortfolioData {
  loading: boolean;
  /**
   * True while a read that could still price the book is out: the warehouse
   * layer, the unpriced fill or the quote units, each landing after `loading`
   * clears. Until then a holding they price is left out of every total (or,
   * before its unit lands, mis-scaled), so a page says its prices are still
   * loading (UnpricedNote) and nothing records the book (audit DATA-10).
   * False when `prices` is off.
   */
  pricing: boolean;
  error: string | null;
  trades: Trade[];
  setTrades: Dispatch<SetStateAction<Trade[]>>;
  tradesByPortfolio: Record<string, Trade[]>;
  settings: Settings;
  setSettings: Dispatch<SetStateAction<Settings>>;
  prices: PriceStore;
  customPrices: CustomPrices;
  fxStore: FxStore;
  activePortfolioId: string;
  setActivePortfolioId: Dispatch<SetStateAction<string>>;
}

const NO_CODES: ReadonlySet<string> = new Set();

/** `prev` with `codes` added; `prev` itself when none is new, so a settled read
 *  re-renders nothing. */
function withCodes(prev: ReadonlySet<string>, codes: string[]): ReadonlySet<string> {
  if (codes.every((c) => prev.has(c))) return prev;
  const next = new Set(prev);
  for (const c of codes) next.add(c);
  return next;
}

function describeError(e: unknown, fallback: string): string {
  return e instanceof PortfolioApiError
    ? `${e.message} — check that you're signed in.`
    : e instanceof Error ? e.message : fallback;
}

export function usePortfolioData(options: UsePortfolioDataOptions = {}): PortfolioData {
  const { allPortfolios = false, prices: withPrices = true, trades: withTrades = true } = options;
  const withFx = options.fx ?? withPrices;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  // Per-portfolio trade slices — populated for the synthetic "All Portfolios"
  // view and whenever `allPortfolios: true`. For a single fund without the
  // option this stays {} and the per-fund `trades` above is used.
  const [tradesByPortfolio, setTradesByPortfolio] = useState<Record<string, Trade[]>>({});
  const [settings, setSettings] = useState<Settings>({});
  // The composed book, and a FLOOR under it: warehouse series for held codes
  // the book cannot price (see the unpriced-held effect below). Kept apart so
  // the non-blocking warehouse recompose can never erase the floor.
  const [book, setPrices] = useState<PriceStore>({});
  const [unpricedFloor, setUnpricedFloor] = useState<PriceStore>({});
  const unpricedAttempted = useRef(new Set<string>());
  // Each held code's quote currency, read once per code per mount (see the
  // quote-unit effect below); the market's closes are put into the unit the
  // code was traded in before any engine reads them.
  const [quoteUnits, setQuoteUnits] = useState<Record<string, string>>({});
  const unitsAttempted = useRef(new Set<string>());
  // What `pricing` reads: warehouse-layer reads still out, and the codes whose
  // unpriced fill and quote unit have been answered (a series, nothing or a
  // failure alike). State, not the refs above, so a code not yet asked reads
  // as still loading on the very render that clears `loading`.
  const [warehousePending, setWarehousePending] = useState(0);
  const [fillAnswered, setFillAnswered] = useState(NO_CODES);
  const [unitsAnswered, setUnitsAnswered] = useState(NO_CODES);
  const [customPrices, setCustomPrices] = useState<CustomPrices>({});
  // Shared across the whole app so one selector drives every page/tab.
  const { activePortfolioId, setActivePortfolioId, bootstrappedRef } = useActivePortfolio();
  // Ref mirror so the mount effect can read the CURRENT selection without listing
  // activePortfolioId as a dep (re-fetching on a switch is the switch effect's job).
  const activePortfolioIdRef = useRef(activePortfolioId);
  activePortfolioIdRef.current = activePortfolioId;
  const [fxStore, setFxStore] = useState<FxStore>({});
  const units = useMemo(
    () => tradeUnits([trades, ...Object.values(tradesByPortfolio)]),
    [trades, tradesByPortfolio],
  );
  // The owner's own series keep the unit the owner gave them: a custom series,
  // a warehouse link or a price link may be merged date by date with the
  // market's, so no one factor is right for it (alignQuoteUnits).
  const configured = useMemo(() => new Set([
    ...Object.keys(customPrices),
    ...Object.keys(settings.warehouseLinks ?? {}),
    ...Object.keys(settings.priceLinks ?? {}),
  ]), [customPrices, settings.warehouseLinks, settings.priceLinks]);
  const prices = useMemo(
    () => alignQuoteUnits(fillUnpriced(book, unpricedFloor), quoteUnits, units, fxStore, configured),
    [book, unpricedFloor, quoteUnits, units, fxStore, configured],
  );

  // Concurrency trackers — race rules live in lib/dataIntake.ts; this hook
  // only owns React state. One tracker per load slot, created once per instance.
  const [loadGens] = useState(createLoadGenerations);
  const [switchGens] = useState(createLoadGenerations);

  // Mount data load — ticket pattern (see module header + lib/dataIntake.ts).
  useEffect(() => {
    const ticket = loadGens.begin();
    const stale = () => ticket.isStale();
    (async () => {
      setLoading(true); setError(null);
      try {
        const s = await getSettings();
        if (stale()) return;
        setSettings(s);
        // Seed the shared selection from settings ONCE; after that, respect the
        // user's current choice so navigating between pages/tabs doesn't reset it.
        const seeded = s.activePortfolioId
          ?? (s.portfolios && s.portfolios.length > 0 ? s.portfolios[0].id : '');
        const id = bootstrappedRef.current ? activePortfolioIdRef.current : seeded;
        if (!bootstrappedRef.current) {
          bootstrappedRef.current = true;
          if (id !== activePortfolioIdRef.current) setActivePortfolioId(id);
        }
        // "All Portfolios" has no server-side trade list; load every fund's
        // slice instead (the page aggregates), and skip getTrades('__all__').
        const all = isAllPortfolios(id);
        const [t, fx, byPortfolio, custom] = await Promise.all([
          withTrades && !all ? getTrades(id || undefined) : Promise.resolve([] as Trade[]),
          withFx ? getFxStore() : Promise.resolve({} as FxStore),
          all || allPortfolios
            ? getAllPortfolioTrades(s.portfolios ?? [])
            : Promise.resolve({} as Record<string, Trade[]>),
          withPrices ? getCustomPrices() : Promise.resolve({} as CustomPrices),
        ]);
        if (stale()) return;
        setTrades(t);
        setCustomPrices(custom);
        setFxStore(fx); setTradesByPortfolio(byPortfolio);
        if (withPrices) {
          // No base layer: the `priceStore` blob held only quote snapshots,
          // which never reached the dated book, so the book is built from the
          // self-priced series here, the warehouse layer below and the unpriced
          // fill (WORK.md item 52 step A2). Quotes come from /api/symbols/quotes.
          setPrices(composePriceBook({ base: {}, custom, priceLinks: s.priceLinks }));
          // Warehouse layer arrives non-blocking: the page paints with the
          // base book and upgrades when the (server-KV-cached) series land,
          // reading as still pricing until they do. Counted on this render,
          // with `loading` cleared, so no render reads the book as priced.
          if (Object.keys(s.warehouseLinks ?? {}).length > 0) {
            setWarehousePending((n) => n + 1);
            void (async () => {
              try {
                const warehouse = await loadWarehouseSeries(s.warehouseLinks);
                if (stale() || Object.keys(warehouse).length === 0) return;
                setPrices(composePriceBook({ base: {}, custom, warehouse, priceLinks: s.priceLinks }));
              } finally {
                setWarehousePending((n) => n - 1);
              }
            })();
          }
        }
      } catch (e) {
        // settle() drops the error when this run was superseded — a stale
        // run may neither write data nor raise its error.
        const transition = ticket.settle({ ok: false, error: describeError(e, 'Failed to load') });
        if (transition.action === 'fail') setError(transition.error);
      } finally {
        if (!stale()) setLoading(false);
      }
    })();
    // Cleanup supersedes this run so a torn-down run can't write stale data;
    // the re-run (if any) owns `loading` from then on.
    return () => { loadGens.supersede(); };
  }, [allPortfolios, withPrices, withFx, withTrades, loadGens, setActivePortfolioId, bootstrappedRef]);

  // Latest tradesByPortfolio / portfolios, read inside the switch effect
  // without being deps (which would re-fetch on every settings/slice update).
  const tradesByPortfolioRef = useRef(tradesByPortfolio);
  tradesByPortfolioRef.current = tradesByPortfolio;
  const portfoliosRef = useRef(settings.portfolios);
  portfoliosRef.current = settings.portfolios;

  // Reload the active portfolio's trades on portfolio change. Same ticket
  // pattern, own tracker: a portfolio switch must not stale the mount load
  // (or vice versa) — only a NEWER switch supersedes an in-flight one.
  useEffect(() => {
    if (!activePortfolioId) return;
    const ticket = switchGens.begin();
    (async () => {
      try {
        if (isAllPortfolios(activePortfolioId)) {
          // The aggregate has no server-side list; refresh every fund's slice
          // (the page combines them). Guard the mount race: on first paint
          // settings may not be set yet, but the initial load above already
          // fetched the slices.
          const portfolios = portfoliosRef.current ?? [];
          if (portfolios.length > 0) {
            const byPortfolio = await getAllPortfolioTrades(portfolios);
            if (!ticket.isStale()) setTradesByPortfolio(byPortfolio);
          }
        } else if (withTrades) {
          // Instant switch: paint the already-loaded snapshot for this
          // portfolio immediately (when `allPortfolios` fetched it at mount),
          // so the page updates without a network round-trip showing the
          // PREVIOUS portfolio's numbers in the gap. The fetch below then
          // reconciles it.
          const snapshot = tradesByPortfolioRef.current[activePortfolioId];
          if (snapshot) setTrades(snapshot);
          const t = await getTrades(activePortfolioId);
          if (!ticket.isStale()) setTrades(t);
        }
      } catch (e) {
        // Surface the switch failure instead of silently keeping the previous
        // portfolio's trades (which read as stale data with no error shown) —
        // unless this run was superseded, in which case settle() drops it.
        const transition = ticket.settle({ ok: false, error: describeError(e, 'Failed to switch portfolio') });
        if (transition.action === 'fail') setError(transition.error);
      }
    })();
    return () => { switchGens.supersede(); };
  }, [activePortfolioId, withTrades, switchGens]);

  // Held codes the book cannot price (WORK.md item 45). A code with no custom
  // series has no dated close in the book (the priceStore blob, read until
  // WORK.md item 52 step A2, held only quotes), and the warehouse layer above
  // is fetched only for settings.warehouseLinks — so the code dropped out of every valuation,
  // silently, and the rebalance 422'd on "at least two projectable holdings".
  // The rule is general, not an L&G list: ANY code the log ever held with no
  // dated close is looked up by its own code, once per code per mount, and the
  // series fills only that gap (a priced code is never overridden). EVER held,
  // not held today: the curve values the book on past dates, and a code sold
  // out since was worth something on every date it was held (data audit
  // DATA-1, 2026-10-10 — the pension's main fund of 2019-2022 read ~£0).
  const needFill = useMemo(() => {
    if (!withPrices) return [];
    const held = new Set<string>();
    for (const slice of [trades, ...Object.values(tradesByPortfolio)]) {
      for (const t of everHeldTickers(slice)) held.add(t);
    }
    return unpricedHeld(book, held);
  }, [withPrices, trades, tradesByPortfolio, book]);
  useEffect(() => {
    if (!withPrices || loading) return;
    const missing = needFill.filter((t) => !unpricedAttempted.current.has(t));
    if (missing.length === 0) return;
    for (const t of missing) unpricedAttempted.current.add(t);
    void loadUnpricedSeries(missing).then((series) => {
      if (Object.keys(series).length > 0) setUnpricedFloor((prev) => ({ ...prev, ...series }));
    }).finally(() => setFillAnswered((prev) => withCodes(prev, missing)));
  }, [withPrices, loading, needFill]);

  // The quote currency of every code the log ever held (audit MONEY-9): the
  // market quotes most LSE listings in pence while a trade inferred from
  // `.LSE` is in pounds, and every engine multiplies a close by its holding's
  // rate, so an unaligned pence close is a holding worth 100 times its value.
  // A failed read leaves the codes unaligned, as every read before this one
  // did, and is not retried this mount.
  useEffect(() => {
    if (!withPrices || loading) return;
    const missing = Object.keys(units).filter((t) => !unitsAttempted.current.has(t));
    if (missing.length === 0) return;
    for (const t of missing) unitsAttempted.current.add(t);
    void fetchListingStatus(missing).then(
      ({ currencies }) => {
        if (Object.keys(currencies).length > 0) setQuoteUnits((prev) => ({ ...prev, ...currencies }));
      },
      () => { /* units unknown: the series stay as quoted */ },
    ).finally(() => setUnitsAnswered((prev) => withCodes(prev, missing)));
  }, [withPrices, loading, units]);

  // A code not yet answered is still loading whether or not it has been asked:
  // the effects above ask only after the render that clears `loading`.
  const pricing = withPrices && (
    loading
    || warehousePending > 0
    || needFill.some((t) => !fillAnswered.has(t))
    || Object.keys(units).some((t) => !unitsAnswered.has(t))
  );

  return {
    loading, pricing, error, trades, setTrades, tradesByPortfolio,
    settings, setSettings, prices, customPrices, fxStore,
    activePortfolioId, setActivePortfolioId,
  };
}
